import React from 'react';
import PropTypes from 'prop-types';
import SunburstChart from './SunburstChart';
import { METRIC_LABELS } from './Constants';

const NOOP = () => {};
const EMPTY = [];   // stable empty highlight so the teaching ring stays isolated from the live wheel

// Zone A of "The Read" (DER_READ_PAGE_SPEC.md): ONE connected unit — the insight (dominant), with its
// own ring right beside it as the SUPPORTING EVIDENCE, and the wheel's grammar folded in as fine print.
// Bounded in a single card with a verdict-coloured accent bar so the claim + its proof read as one thing,
// not two stacked blocks. Zone B (ReadTransition) is the tinted seam into the live tool below.

const NAVY = '#14536e';
const GREEN = '#1f7a5a';
const PEACH = '#b06a14';
const RED = '#a23a2e';
const SLATE = '#33454f';
const MUTE = '#8296a3';
const BAND_LABEL = { keep: 'In good shape', polish: 'Polish', rework: 'Needs a rework', rebuild: 'Big rebuild', cut_or_combine: 'Cut / combine' };
const BAND_COLOR = { keep: GREEN, polish: '#3b6fb0', rework: PEACH, rebuild: RED, cut_or_combine: RED };

// The concrete stretch to start in — prefer a range the note names (first_move, e.g. "26–36");
// never a whole-script span like "1–42" (starts at 1), which is the diffuse footprint and useless.
function coolRange(g) {
  const src = `${g.first_move || ''} ${(g.footprint && g.footprint.summary) || ''}`;
  const m = /scenes?\s+(\d+)\s*[–-]\s*(\d+)/i.exec(src);
  return (m && +m[1] > 1) ? `${+m[1]}–${+m[2]}` : null;
}

export function ReadHeader({ synthesis, movie, ringMetric }) {
  const g = synthesis && synthesis.writer_view && synthesis.writer_view.tier_1_glance;
  if (!g) return null;
  const band = g.verdict_band;
  const accent = BAND_COLOR[band] || NAVY;
  const metricLabel = ringMetric && ringMetric !== 'overall'
    ? (METRIC_LABELS[ringMetric] || ringMetric)
    : (ringMetric === 'overall' ? 'Triage' : null);
  const range = coolRange(g);
  return (
    <div style={{ maxWidth: 1100, margin: '1.2rem auto 0', padding: '0 1.15rem' }}>
      {/* Frames the big headline as THE lead finding (not a page title). Readable navy, not a faint
          eyebrow — the label is as visible as the content it introduces. */}
      <div style={{ color: NAVY, fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4, margin: '0 0 .55rem' }}>
        The one thing that would help your script most:
      </div>
      {/* The on-ramp (what Layered Read is) is the reusable PageIntro banner, above, by ScriptSnout.
          One bounded unit here: claim + its evidence share a card and a verdict-coloured accent bar. */}
      <div style={{ border: '1px solid #e2e8f0', borderLeft: `5px solid ${accent}`, borderRadius: 14,
                    background: '#fbfdff', boxShadow: '0 1px 3px rgba(15,23,42,.05)', padding: '1.2rem 1.4rem' }}>
        {/* THE INSIGHT — dominant */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ color: NAVY, fontWeight: 800, fontSize: '1.55rem', margin: 0, lineHeight: 1.2 }}>{g.thesis_title}</h2>
          {band && (
            <span style={{ fontSize: '.76rem', fontWeight: 800, color: accent, background: '#f1f5f9', borderRadius: 20, padding: '2px 10px', whiteSpace: 'nowrap' }}>
              {BAND_LABEL[band] || band}{g.confidence ? ` · ${g.confidence} confidence` : ''}
            </span>
          )}
        </div>
        {g.plain_thesis && <p style={{ margin: '.5rem 0 .7rem', fontSize: '1.02rem', color: '#1f2d35', lineHeight: 1.55, maxWidth: 820 }}>{g.plain_thesis}</p>}
        {g.first_move && (
          <div style={{ fontSize: '.95rem', color: '#1f2d35', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 800, color: NAVY }}>▶ Start here: </span>{g.first_move}
          </div>
        )}
        {g.protect_short && (
          <div style={{ marginTop: '.3rem', fontSize: '.92rem', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 800, color: GREEN }}>Protect: </span><span style={{ color: SLATE }}>{g.protect_short}</span>
          </div>
        )}

        {/* THE EVIDENCE — the ring sits BESIDE the claim (not below), so it reads as the proof for it.
            The interpretive line ties it back to the claim; the wheel's grammar is fine print. */}
        <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.6rem', alignItems: 'center' }}>
          <div style={{ width: 300 }}>
            {movie && ringMetric && (
              <SunburstChart movie={movie} includeScenes metric={ringMetric} size={300}
                             interactive={false} levelHighlight={EMPTY} onSelect={NOOP} autoSelectRoot={false} />
            )}
          </div>
          <div>
            <div style={{ color: accent, fontWeight: 800, fontSize: '.8rem', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>
              You can see it{metricLabel ? <> — coloured by {metricLabel}</> : ''}
            </div>
            <p style={{ margin: 0, color: SLATE, lineHeight: 1.55 }}>
              {metricLabel
                ? <><b style={{ color: NAVY }}>{metricLabel}</b> is your biggest lever — the single change that would lift the script most. Where the red reaches the inner rings it’s structural; where only the outer scenes are red, the sequence still holds. It runs deepest{range ? <> around scenes&nbsp;{range}</> : ''}, so that’s where to start.</>
                : <>Look for where the colour runs deepest{range ? <> (scenes&nbsp;{range})</> : ''} — that’s where to start.</>}
            </p>
            <p style={{ margin: '.7rem 0 0', color: MUTE, fontSize: '.82rem', lineHeight: 1.5 }}>
              How to read it: each wedge is a unit; 12 o’clock = Scene&nbsp;1, clockwise = story order, rings go
              scene ▸ sequence ▸ act outward, colour <b style={{ color: GREEN }}>green</b> (working) →
              <b style={{ color: RED }}> red</b> (needs work).{metricLabel ? <> Pick <b>{metricLabel}</b> in Colour&nbsp;by, below, to explore it.</> : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
ReadHeader.propTypes = { synthesis: PropTypes.object, movie: PropTypes.object, ringMetric: PropTypes.string };

// Zone B — the seam from the static read into the live tool. A distinct tinted band so the shift from
// READING (Zone A, above) to EXPLORING (the wheel, below) is unmistakable — names both entry points.
export function ReadTransition() {
  return (
    <div style={{ background: '#eef4f8', borderTop: '1px solid #dbe6ee', borderBottom: '1px solid #dbe6ee',
                  margin: '2.4rem 0 0', padding: '1.15rem 1.15rem 1.25rem' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: NAVY, fontWeight: 800, fontSize: '.84rem', letterSpacing: '.05em', textTransform: 'uppercase' }}>
          ↓ That’s the headline — here’s the whole read to explore
        </div>
        <p style={{ margin: '.5rem auto 0', color: '#456', fontSize: '.93rem', lineHeight: 1.5 }}>
          Click a pattern to light exactly those units on the wheel, click a wedge to open its own notes,
          or use <b style={{ color: NAVY }}>Colour by</b> to repaint the wheel by a single criterion.
        </p>
      </div>
    </div>
  );
}

export default ReadHeader;
